import { useEffect, useRef, useState } from 'react';

/**
 * WebSocket-подключение к комнате проекта (docs/05 §11, UC-RTM-01).
 *
 * - Протокол наследуется от текущего origin (`ws`/`wss`).
 * - Экспоненциальный backoff 1s → 2s → 4s … → 30s.
 * - Ping каждые 30 с; сервер отвечает `pong`.
 * - Свои события (actorId === currentUser.id) не применяются повторно.
 * - При reconnect кэш инвалидируется целиком (refetch).
 */

import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { ServerEventSchema, type ServerEvent } from '@tasks/contracts';

import { getCurrentUser } from '../stores/sessionStore';
import { useFloatingStore } from '../stores/floatingStore';
import { logger } from '../lib/logger';
import { applyServerEvent } from './applyServerEvent';

const BASE_DELAY_MS = 1_000;
const MAX_DELAY_MS = 30_000;
const PING_INTERVAL_MS = 30_000;

export type SocketStatus = 'connecting' | 'open' | 'closed';

export type PresenceHandler = (issueId: string, userIds: readonly string[]) => void;

function socketUrl(projectKey: string): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/api/ws/projects/${encodeURIComponent(projectKey)}`;
}

function isOwnEvent(event: ServerEvent): boolean {
  const currentUser = getCurrentUser();
  if (!currentUser || event.type === 'pong') {
    return false;
  }
  const payload: unknown = event.payload;
  if (typeof payload !== 'object' || payload === null || !('actorId' in payload)) {
    return false;
  }
  const actorId = (payload as { actorId?: unknown }).actorId;
  return actorId === currentUser.id;
}

/** Reconnect — единственный случай, когда вид перезапрашивается целиком. */
function invalidateProject(queryClient: QueryClient, projectKey: string): void {
  queryClient.invalidateQueries({ queryKey: ['projects', projectKey] });
  queryClient.invalidateQueries({ queryKey: ['issues'] });
}

export type UseProjectSocketOptions = {
  readonly projectKey: string | null;
  readonly onPresence?: PresenceHandler;
};

export function useProjectSocket({ projectKey, onPresence }: UseProjectSocketOptions): SocketStatus {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<SocketStatus>('closed');
  const presenceRef = useRef<PresenceHandler | undefined>(onPresence);
  presenceRef.current = onPresence;

  useEffect(() => {
    if (!projectKey) {
      setStatus('closed');
      return;
    }

    let socket: WebSocket | null = null;
    let retries = 0;
    let isDisposed = false;
    let pingTimer: number | undefined;
    let retryTimer: number | undefined;
    let hasConnectedBefore = false;

    const sendPing = (): void => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'ping' }));
      }
    };

    const handleEvent = (event: ServerEvent): void => {
      if (event.type === 'pong') {
        return;
      }
      if (event.type === 'presence.updated') {
        presenceRef.current?.(event.payload.issueId, event.payload.userIds);
        return;
      }
      // Уведомление адресовано конкретному пользователю: чужое не применяем.
      if (event.type === 'notification.created') {
        const currentUser = getCurrentUser();
        if (!currentUser || event.payload.userId !== currentUser.id) {
          return;
        }
        applyServerEvent(queryClient, projectKey, event);
        return;
      }
      // Своё действие уже применено оптимистично (docs/05 §11).
      if (isOwnEvent(event)) {
        return;
      }
      // Карточку тащат — откладываем, чтобы она не «выпрыгнула» из-под курсора.
      if (useFloatingStore.getState().isDraggingCard) {
        useFloatingStore.getState().deferEvent(event);
        return;
      }
      applyServerEvent(queryClient, projectKey, event);
    };

    const scheduleReconnect = (): void => {
      if (isDisposed) {
        return;
      }
      const delay = Math.min(BASE_DELAY_MS * 2 ** retries, MAX_DELAY_MS);
      retries += 1;
      retryTimer = window.setTimeout(connect, delay);
    };

    const applyDeferred = (): void => {
      const deferred = useFloatingStore.getState().takeDeferredEvents();
      for (const event of deferred) {
        handleEvent(event);
      }
    };

    const unsubscribeDeferred = useFloatingStore.subscribe((state, previous) => {
      if (previous.isDraggingCard && !state.isDraggingCard) {
        applyDeferred();
      }
    });

    function connect(): void {
      if (isDisposed) {
        return;
      }
      setStatus('connecting');
      socket = new WebSocket(socketUrl(projectKey as string));

      socket.onopen = () => {
        if (isDisposed) {
          return;
        }
        const wasReconnect = hasConnectedBefore;
        hasConnectedBefore = true;
        retries = 0;
        setStatus('open');
        if (wasReconnect) {
          invalidateProject(queryClient, projectKey as string);
        }
        pingTimer = window.setInterval(sendPing, PING_INTERVAL_MS);
      };

      socket.onmessage = (message: MessageEvent<string>) => {
        let parsed: unknown;
        try {
          parsed = JSON.parse(message.data);
        } catch {
          logger.warn('ws: не удалось разобрать сообщение');
          return;
        }
        const envelope = parsed as { type?: unknown; payload?: unknown };
        // Сервер кладёт событие в конверт { type, projectId, ts, payload }.
        const candidate =
          typeof envelope.type === 'string' && envelope.payload !== undefined
            ? { type: envelope.type, payload: envelope.payload }
            : parsed;
        const result = ServerEventSchema.safeParse(candidate);
        if (!result.success) {
          logger.warn('ws: неизвестное событие', envelope.type);
          return;
        }
        handleEvent(result.data);
      };

      socket.onerror = () => {
        logger.warn('ws: ошибка соединения');
      };

      socket.onclose = () => {
        if (pingTimer !== undefined) {
          window.clearInterval(pingTimer);
          pingTimer = undefined;
        }
        if (isDisposed) {
          return;
        }
        setStatus('closed');
        scheduleReconnect();
      };
    }

    connect();

    return () => {
      isDisposed = true;
      unsubscribeDeferred();
      if (pingTimer !== undefined) {
        window.clearInterval(pingTimer);
      }
      if (retryTimer !== undefined) {
        window.clearTimeout(retryTimer);
      }
      socket?.close();
      setStatus('closed');
    };
  }, [projectKey, queryClient]);

  return status;
}
